import { AriseBridgeHost } from '@/arise/bridge/AriseBridgeHost';
import { getCurrentUserId } from '@/arise/api';
import { getQuestId } from '@/components/DiscordAuthFlow/useDiscord';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { Badge, ProfileV2, Title } from '@/models/profile';
import { Translations } from '@/models/translations';
import { useAppSelector } from '@/state/store';
import { useEffect, useRef } from 'react';

export default function usePushUserData(bridge: AriseBridgeHost) {
    const userDataRef = useRef<{
        langIso?: null | string;
        currentUserProfileV2?: null | ProfileV2;
        availableBadges?: null | Badge[];
        availableTitles?: null | Title[];
        translations?: null | Translations;
        discordLocale?: null | string;
        discordPremiumType?: null | number;
        discordUserId?: null | string;
        questId?: any;
        questStatus?: any;
        questCTAURL?: any;
    }>({});

    const currentUserProfileV2 = useAppSelector((state) => state.profile.currentUserProfileV2);
    const availableBadges = useAppSelector((state) => state.profile.availableBadges);
    const availableTitles = useAppSelector((state) => state.profile.availableTitles);
    const langIso = useAppSelector((state) => state.app.langIso);
    const translations = useAppSelector((state) => state.app.translations);
    const discordLocale = useAppSelector((state) => state.discord.locale);
    const discordPremiumType = useAppSelector((state) => state.discord.user?.premium_type);
    const discordUserId = useAppSelector((state) => state.discord.user?.id);
    const questStatus = useAppSelector((state) => state.discord.questStatus);
    const questCTAURL = useAppSelector((state) => state.discord.questCTAURL);
    const shouldArisewareBeMuted = useAppSelector((state) => state.app.shouldArisewareBeMuted);

    userDataRef.current = {
        langIso,
        currentUserProfileV2,
        availableBadges,
        availableTitles,
        translations,
        discordLocale,
        discordPremiumType,
        discordUserId,
        questStatus,
        questCTAURL,
    };

    function pushUserProfile() {
        const { currentUserProfileV2: profile, discordUserId } = userDataRef.current;
        if (profile) {
            bridge?.sendEvent('user-data', {
                ...profile,
                // Fall back to the id from the auth token in case the profile hasn't been loaded yet
                userId: profile.userId || getCurrentUserId(),
                ...(discordUserId && { discordUserId }),
            });
        }
    }

    function pushAvailableBadgesData() {
        if (userDataRef.current.availableBadges) {
            bridge?.sendEvent('available-badges-data', userDataRef.current.availableBadges);
        }
    }

    function pushAvailableTitlesData() {
        if (userDataRef.current.availableTitles) {
            bridge?.sendEvent('available-titles-data', userDataRef.current.availableTitles);
        }
    }

    function pushLocale() {
        if (userDataRef.current.langIso) {
            bridge?.sendEvent('locale', { locale: userDataRef.current.langIso });
        }
    }

    function pushDiscordLocale() {
        if (IS_DISCORD_ACTIVITY && userDataRef.current.discordLocale) {
            bridge?.sendEvent('discord-locale', { locale: userDataRef.current.discordLocale });
        }
    }

    function pushTranslations() {
        if (userDataRef.current.translations) {
            bridge?.sendEvent('translations', { translations: userDataRef.current.translations });
        }
    }

    function pushDiscordPremiumType() {
        if (userDataRef.current.discordPremiumType || userDataRef.current.discordPremiumType === 0) {
            bridge?.sendEvent('discord-premium-type', userDataRef.current.discordPremiumType);
        }
    }

    function pushQuestInfo() {
        const { questStatus, questCTAURL } = userDataRef.current;
        const questId = getQuestId();
        bridge?.sendEvent('quest-info', { questId, questStatus, questCTAURL });
    }

    function pushMuteState() {
        if (typeof shouldArisewareBeMuted === 'boolean') {
            bridge?.sendEvent('mute', shouldArisewareBeMuted);
        }
    }

    function pushUserData() {
        pushUserProfile();
        pushAvailableBadgesData();
        pushAvailableTitlesData();
        pushLocale();
        pushDiscordLocale();
        pushTranslations();
        pushDiscordPremiumType();
        pushQuestInfo();
        pushMuteState();
    }

    useEffect(() => {
        pushUserProfile();
    }, [currentUserProfileV2, discordUserId]);

    useEffect(() => {
        pushAvailableBadgesData();
    }, [availableBadges]);

    useEffect(() => {
        pushAvailableTitlesData();
    }, [availableTitles]);

    useEffect(() => {
        pushLocale();
    }, [langIso]);

    useEffect(() => {
        pushTranslations();
    }, [translations]);

    useEffect(() => {
        pushDiscordLocale();
    }, [discordLocale]);

    useEffect(() => {
        pushDiscordPremiumType();
    }, [discordPremiumType]);

    useEffect(() => {
        pushQuestInfo();
    }, [questStatus, questCTAURL]);

    useEffect(() => {
        pushMuteState();
    }, [shouldArisewareBeMuted]);

    return { pushUserData, pushLocale, pushTranslations, pushDiscordLocale, pushDiscordPremiumType, pushQuestInfo };
}
